const normalize = (value) => String(value || '').trim().toLowerCase()

const ELIGIBLE_STATUSES = new Set(['waitlist', 'inquired'])

const isEligibleStatus = (value) => ELIGIBLE_STATUSES.has(normalize(value))

export const TEACHER_WAITLIST_DRIVE_BUCKET = '17:00-18:00'

export const formatTeacherWaitlistAddress = (address = {}) => {
    if (typeof address === 'string') return address.trim()
    if (!address || typeof address !== 'object') return ''

    const normalizedAddress = address.Address || address.address || address
    return [
        normalizedAddress?.addressLine1
            || normalizedAddress?.AddressLine1
            || normalizedAddress?.StreetAddress
            || normalizedAddress?.streetAddress
            || normalizedAddress?.street,
        normalizedAddress?.addressLine2
            || normalizedAddress?.AddressLine2
            || normalizedAddress?.unit,
        normalizedAddress?.city || normalizedAddress?.City,
        normalizedAddress?.state || normalizedAddress?.State,
        normalizedAddress?.zipcode
            || normalizedAddress?.Zipcode
            || normalizedAddress?.Zip
            || normalizedAddress?.zip
            || normalizedAddress?.zipCode
            || normalizedAddress?.postalCode,
    ].map((value) => String(value || '').trim()).filter(Boolean).join(', ')
}

export const getTeacherWaitlistResponseRows = (response, property) => {
    const payload = response?.data
    if (Array.isArray(payload)) return payload
    return Array.isArray(payload?.[property]) ? payload[property] : []
}

const hasUsableCoordinates = (directory) => (
    directory?.lat !== null
    && directory?.lat !== undefined
    && directory?.lat !== ''
    && directory?.lng !== null
    && directory?.lng !== undefined
    && directory?.lng !== ''
    && Number.isFinite(Number(directory.lat))
    && Number.isFinite(Number(directory.lng))
)

export const findTeacherWaitlistDirectoryKey = ({
    directories = [],
    entityType,
    entityId,
    fallbackKey,
} = {}) => {
    const targetId = String(entityId || '').trim()
    const targetKey = normalize(fallbackKey)
    const associationField = entityType === 'teacher' ? 'teacherIds' : 'familyIds'

    const match = (Array.isArray(directories) ? directories : []).find((directory) => {
        if (!hasUsableCoordinates(directory)) return false
        const associatedIds = Array.isArray(directory?.[associationField])
            ? directory[associationField].map(normalize)
            : []
        return (
            (targetId && associatedIds.includes(normalize(targetId)))
            || (targetKey && normalize(directory?.key) === targetKey)
            || (targetId && normalize(directory?.key) === normalize(targetId))
        )
    })

    return match?.key || ''
}

export const findCachedTeacherWaitlistDriveMinutes = ({
    edges = [],
    schoolId,
    fromKey,
    toKey,
    bucket = TEACHER_WAITLIST_DRIVE_BUCKET,
} = {}) => {
    const matchingEdges = (Array.isArray(edges) ? edges : []).filter((edge) => (
        (!edge?.schoolId || normalize(edge.schoolId) === normalize(schoolId))
        && normalize(edge?.origin?.key || edge?.origin || edge?.fromKey || edge?.from) === normalize(fromKey)
        && normalize(edge?.destination?.key || edge?.destination || edge?.toKey || edge?.to) === normalize(toKey)
    ))
    const exactBucketEdges = matchingEdges.filter(
        (edge) => normalize(edge?.todBucket || edge?.bucket) === normalize(bucket)
    )
    const candidates = [...exactBucketEdges, ...matchingEdges.filter(
        (edge) => !exactBucketEdges.includes(edge)
    )]

    for (const edge of candidates) {
        const rawMinutes = edge?.minutes
        if (rawMinutes === null || rawMinutes === undefined || rawMinutes === '') continue
        const minutes = Number(rawMinutes)
        if (Number.isFinite(minutes) && minutes >= 0) return minutes
    }
    return null
}

const parseDriveTimeMinutes = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Number.isFinite(Number(value))) return Math.max(0, Math.round(Number(value)))

    const text = String(value).trim().toLowerCase()
    const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:hour|hr)/)
    const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:minute|min)/)
    if (!hourMatch && !minuteMatch) return null
    const hours = hourMatch ? Number(hourMatch[1]) : 0
    const minutes = minuteMatch ? Number(minuteMatch[1]) : 0
    const total = (hours * 60) + minutes
    return Number.isFinite(total) ? Math.max(0, Math.round(total)) : null
}

export const buildLegacyTeacherWaitlistDriveMinutes = (rows = []) => {
    const minutesByFamily = new Map()

    ;(Array.isArray(rows) ? rows : []).forEach((row) => {
        const familyId = String(
            row?.familyId
            || row?.family?.familyId
            || row?.family?.family?.familyId
            || ''
        ).trim()
        const minutes = parseDriveTimeMinutes(
            row?.sortingDuration ?? row?.minutes ?? row?.duration
        )
        if (familyId && minutes !== null && !minutesByFamily.has(familyId)) {
            minutesByFamily.set(familyId, minutes)
        }
    })

    return minutesByFamily
}

export const sortTeacherWaitlistRowsByDriveTime = (rows = []) => (
    [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
        const leftHasDriveTime = Number.isFinite(left?.driveTimeMinutes)
        const rightHasDriveTime = Number.isFinite(right?.driveTimeMinutes)
        if (leftHasDriveTime && rightHasDriveTime) {
            return left.driveTimeMinutes - right.driveTimeMinutes
                || String(left.studentName || '').localeCompare(String(right.studentName || ''))
        }
        if (leftHasDriveTime) return -1
        if (rightHasDriveTime) return 1
        return String(left?.studentName || '').localeCompare(String(right?.studentName || ''))
    })
)

const getInstrumentLabel = (instrument) => {
    if (!instrument) return ''
    if (typeof instrument === 'string') return instrument.trim()

    const parentInstrument = String(
        instrument.parentInstrument || instrument.ParentInstrument || ''
    ).trim()
    const specificInstrument = String(
        instrument.name
        || instrument.Name
        || instrument.instrument
        || instrument.Instrument
        || ''
    ).trim()

    if (parentInstrument && normalize(parentInstrument) !== 'skill') {
        return parentInstrument
    }
    return specificInstrument || parentInstrument
}

const getInstrumentTokens = (instrument) => {
    if (!instrument) return []
    if (typeof instrument === 'string') return [normalize(instrument)].filter(Boolean)

    const values = [
        instrument.id,
        instrument._id,
        getInstrumentLabel(instrument),
    ]

    return Array.from(new Set(values.map(normalize).filter(Boolean)))
}

const instrumentsMatch = (studentInstrument, teacherTokens) => (
    getInstrumentTokens(studentInstrument).some((token) => teacherTokens.has(token))
)

const getStudentKey = (family, student, index) => String(
    student?.studentId
    || student?._id
    || `${family?.familyId || family?._id || 'family'}-${index}`
)

export const buildTeacherWaitlistRows = ({ families = [], teacherInstruments = [] } = {}) => {
    const teacherTokens = new Set(
        (Array.isArray(teacherInstruments) ? teacherInstruments : [])
            .flatMap(getInstrumentTokens)
    )

    if (teacherTokens.size === 0) return []

    const rows = []

    ;(Array.isArray(families) ? families : []).forEach((family) => {
        const familyStatusIsEligible = isEligibleStatus(family?.familyStatus)

        ;(Array.isArray(family?.students) ? family.students : []).forEach((student, index) => {
            const studentStatusIsEligible = isEligibleStatus(student?.studentStatus)
            if (!familyStatusIsEligible && !studentStatusIsEligible) return

            const matchingInstruments = (Array.isArray(student?.instrumentList)
                ? student.instrumentList
                : [])
                .filter((instrument) => instrumentsMatch(instrument, teacherTokens))
                .map(getInstrumentLabel)
                .filter(Boolean)

            if (matchingInstruments.length === 0) return

            const address = family?.address || family?.Address || {}
            const city = String(address?.city || address?.City || '').trim()
            const state = String(address?.state || address?.State || '').trim()
            const studentStatus = studentStatusIsEligible
                ? String(student.studentStatus).trim()
                : String(family.familyStatus).trim()

            rows.push({
                id: getStudentKey(family, student, index),
                familyId: String(family?.familyId || family?._id || '').trim(),
                familyName: String(family?.familyName || family?.name || '').trim(),
                studentName: String(student?.studentName || student?.name || 'Unnamed student').trim(),
                instrument: Array.from(new Set(matchingInstruments)).join(', '),
                familyAddress: formatTeacherWaitlistAddress(address),
                location: [city, state].filter(Boolean).join(', ') || 'Location not listed',
                status: studentStatus,
                statusScope: studentStatusIsEligible ? 'student' : 'family',
            })
        })
    })

    return rows.sort((left, right) => (
        left.studentName.localeCompare(right.studentName)
        || left.familyName.localeCompare(right.familyName)
    ))
}
